import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost from "apis/localhost";
import { toIsoDate, toIsoDateTime } from "utils/apiDates";

const baseUrl = (workspaceId) => `/api/cloud-usage/workspaces/${workspaceId}`;

export const fetchWorkspaceHeader = createAsyncThunk(
  "cloudUsageWorkspaceDetail/fetchWorkspaceHeader",
  async (workspaceId, { getState }) => {
    const { filters } = getState().cloudUsageWorkspaceDetail;
    const { data } = await localhost.get(`${baseUrl(workspaceId)}/header`, {
      params: {
        range: filters.range,
        from: filters.from,
        to: filters.to,
        source: filters.source,
      },
    });
    return {
      ...data,
      expiryDate: toIsoDate(data.expiryDate),
      lastActive: toIsoDateTime(data.lastActive),
    };
  }
);

export const fetchWorkspaceByArea = createAsyncThunk(
  "cloudUsageWorkspaceDetail/fetchWorkspaceByArea",
  async (workspaceId, { getState }) => {
    const { filters } = getState().cloudUsageWorkspaceDetail;
    const { data } = await localhost.get(`${baseUrl(workspaceId)}/by-area`, {
      params: filters,
    });
    return data;
  }
);

export const fetchWorkspaceByUser = createAsyncThunk(
  "cloudUsageWorkspaceDetail/fetchWorkspaceByUser",
  async (workspaceId, { getState }) => {
    // Fetch always requests the full membership list — user-selection narrowing (Task 13) is
    // applied client-side against this response, not sent as a request param. Only the export
    // thunk sends the selection to the server.
    const { filters } = getState().cloudUsageWorkspaceDetail;
    const { data } = await localhost.get(`${baseUrl(workspaceId)}/by-user`, {
      params: filters,
    });
    return data;
  }
);

export const exportWorkspaceByAreaThunk = createAsyncThunk(
  "cloudUsageWorkspaceDetail/exportWorkspaceByArea",
  async (workspaceId, { getState }) => {
    const { filters } = getState().cloudUsageWorkspaceDetail;
    const response = await localhost.get(`${baseUrl(workspaceId)}/by-area/export`, {
      params: filters,
      responseType: "blob",
    });
    return response.data;
  }
);

export const exportWorkspaceByUserThunk = createAsyncThunk(
  "cloudUsageWorkspaceDetail/exportWorkspaceByUser",
  async (workspaceId, { getState }) => {
    const { filters, selectedUserNames } = getState().cloudUsageWorkspaceDetail;
    const response = await localhost.get(`${baseUrl(workspaceId)}/by-user/export`, {
      params: { ...filters, userNames: selectedUserNames.join(",") },
      responseType: "blob",
    });
    return response.data;
  }
);

// hooks/useExportToCsv dispatches getExportThunk() as a plain thunk and awaits its return value
// directly as the blob to download — it never calls .unwrap() itself. The two thunks above are
// createAsyncThunk (needed so the isExporting extraReducers below can match
// .pending/.fulfilled/.rejected), so dispatching them raw would resolve to the wrapped
// fulfilled/rejected action object, not the blob. These two bridge that gap for ExportButton's
// getExportThunk prop, without changing the createAsyncThunk shape the reducer/tests rely on.
export const exportWorkspaceByAreaBlob = (workspaceId) => (dispatch) =>
  dispatch(exportWorkspaceByAreaThunk(workspaceId)).unwrap();

export const exportWorkspaceByUserBlob = (workspaceId) => (dispatch) =>
  dispatch(exportWorkspaceByUserThunk(workspaceId)).unwrap();
